import React from 'react';

import { useTranslation } from 'react-i18next';
import { SwiperSlide } from 'swiper/react';

import { useModals } from '@shared/contexts';

import guide1 from '@assets/images/guideImage1.webp';
import guide2 from '@assets/images/guideImage2.webp';
import guide3 from '@assets/images/guideImage3.webp';
import faq from '@assets/images/promotions/faq.webp';

import { useQuerySinglePromotion } from '@hooks/api/promotions/use-query-single-promotion';
import { useQueryUserPromotions } from '@hooks/api/promotions/use-query-user-promotions';

import { Button } from '@components/button';
import { ModalName } from '@components/modals';
import { PromotionBanner } from '@components/promotion-banner';
import { Slider } from '@components/slider';

import { FaqCard } from './components/faq-card';
import { GuideCard } from './components/guide-card';
import { ParticipantsCard } from './components/participants-card';
import { PrizeCard } from './components/prize-card';
import { TicketsCard } from './components/tickets-card';
import * as Styled from './promotion-detailed.styled';

type OwnProps = {
	id: string;
};

interface UserParticipationType {
	isParticipating: boolean;
	ticketsQuantity: number;
}

const PromotionDetailed: React.FC<OwnProps> = ({ id }) => {
	const { data: promo } = useQuerySinglePromotion(id, 1, 1);
	const { data: userPromotions } = useQueryUserPromotions(1, 1);
	const { t } = useTranslation('promotions');
	const { openModal } = useModals();

	const handleParticipantsList = () => {
		openModal(ModalName.PROMOTION_PARTICIPANTS, { participants: promo?.subscriptions, promo_id: promo?.id });
	};

	const handleTicketsBuy = () => {
		openModal(ModalName.PROMOTION_TICKETS, { ticket: promo?.ticket });
	};

	const userParticipation: UserParticipationType = React.useMemo(() => {
		return {
			isParticipating: userPromotions?.lotteries.some(userPromo => userPromo.id === id) ?? false,
			ticketsQuantity: userPromotions?.lotteries.find(promo => promo.id === id)?.tickets_count ?? 0
		};
	}, [id, userPromotions?.lotteries]);

	return (
		<Styled.Wrapper>
			{promo && <PromotionBanner isDetailed promo={promo} isParticipating={userParticipation.isParticipating} />}
			{!userParticipation.isParticipating && (
				<Styled.SectionWrapper>
					<Styled.SectionTitle>{t('GuideTitle')}</Styled.SectionTitle>
					<Styled.GuideCardRow>
						<GuideCard img={guide1} title={t('GuideCardTitle1')} description={t('GuideCardDescription1')} />
						<GuideCard img={guide2} title={t('GuideCardTitle2')} description={t('GuideCardDescription2')} />
						<GuideCard img={guide3} title={t('GuideCardTitle3')} description={t('GuideCardDescription3')} />
					</Styled.GuideCardRow>
				</Styled.SectionWrapper>
			)}
			{promo && userPromotions && userParticipation.isParticipating && (
				<Styled.SectionWrapper>
					<Slider
						navigation={false}
						spaceBetween='16px'
						breakpoints={{
							998: {
								slidesPerView: 2
							},
							0: {
								slidesPerView: 1
							}
						}}
					>
						<SwiperSlide virtualIndex={0}>
							<TicketsCard onClick={handleTicketsBuy} ticketsAmount={userParticipation.ticketsQuantity} />
						</SwiperSlide>
						<SwiperSlide virtualIndex={1}>
							<ParticipantsCard
								participantCount={promo.pagination && promo.pagination.total_count ? promo.pagination.total_count : 0}
								onClick={handleParticipantsList}
							/>
						</SwiperSlide>
					</Slider>
				</Styled.SectionWrapper>
			)}
			{promo && promo.status === 'active' && (
				<Styled.ButtonCenteredWrapper>
					<Button onClick={handleTicketsBuy} size='large' color='light'>
						{t('tickets_buy')}
					</Button>
				</Styled.ButtonCenteredWrapper>
			)}
			{promo && promo.prizes && promo.prizes.length > 0 && (
				<Styled.SectionWrapper>
					<Styled.SectionTitle>{t('main:prizes')}</Styled.SectionTitle>
					<Styled.PrizeSlidesRow>
						<Slider
							spaceBetween='16px'
							pagination={false}
							navigation={false}
							breakpoints={{
								1200: {
									slidesPerView: 4.1
								},
								1000: {
									slidesPerView: 3.1
								},
								800: {
									slidesPerView: 2.1
								},
								0: {
									slidesPerView: 1.1
								}
							}}
						>
							{promo.prizes.map((prize, index) => (
								<SwiperSlide key={index} virtualIndex={index}>
									<PrizeCard prize={prize} />
								</SwiperSlide>
							))}
						</Slider>
					</Styled.PrizeSlidesRow>
				</Styled.SectionWrapper>
			)}
			{promo && !userParticipation.isParticipating && (
				<Styled.SectionWrapper>
					<ParticipantsCard
						participantCount={promo.pagination && promo.pagination.total_count ? promo.pagination.total_count : 0}
						onClick={handleParticipantsList}
					/>
				</Styled.SectionWrapper>
			)}
			<Styled.SectionWrapper>
				<Styled.Faq>
					<Styled.FaqContent>
						<Styled.SectionTitle>{t('faq')}</Styled.SectionTitle>
						<Styled.FaqCards>
							<FaqCard title={t('FaqCardTitle1')} description={t('FaqCardDescription1')} />
							<FaqCard title={t('FaqCardTitle2')} description={t('FaqCardDescription2')} />
							<FaqCard title={t('FaqCardTitle3')} description={t('FaqCardDescription3')} />
						</Styled.FaqCards>
					</Styled.FaqContent>
					<Styled.FaqImageWrapper>
						<img src={faq} alt='faq' />
					</Styled.FaqImageWrapper>
				</Styled.Faq>
			</Styled.SectionWrapper>
		</Styled.Wrapper>
	);
};

export default PromotionDetailed;
